import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GcpConnectorService } from './services/gcp-connector.service';
import { SwStateMachineService } from './services/sw-state-machine.service';
import { Subject, Subscription, takeUntil, tap } from 'rxjs';
import { StateSyncService } from './services/state-sync.service';
import { AnalyticsService } from '../../services/analytics';
import { WindowRefService } from '../share/services/window-ref.service';
import { JwtAuthService } from '../../services/jwt-auth';
import { AsyncPipe, NgStyle } from '@angular/common';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzCardComponent } from 'ng-zorro-antd/card';
import { ListComponent } from './components/list.component';
import { LoadingComponent } from './components/loading.component';
import { ErrorComponent } from './components/error.component';

@Component({
  selector: 'ava-smart-weather',
  standalone: true,
  template: `
    <div #container [style]="'padding: 20px 0'">
      @if (!windowRefService.nativeWindow?.metaData?.user?.memberNumber) {
        <div
          [style]="
            'display: grid; column-gap: 20px; padding: 20px; grid-template-columns: 1fr 1fr; max-width: 50%; margin: auto'
          "
          [ngStyle]="buttonWrapperStyles"
        >
          <button nz-button nzType="primary">
            <a [href]="'/smart-weather-login'">Sign Up</a>
          </button>
          <button nz-button nzType="primary">
            <a [href]="'/smart-weather-login'">Manage Account</a>
          </button>
        </div>
      }

      @if (
        windowRefService.nativeWindow?.metaData?.user?.memberNumber && (jwtAuthService.afAuthIdTokenResult$ | async)
      ) {
        <nz-card
          [nzTitle]="bannerTitle"
          nzBorderless
          class="blue-banner"
          [style]="'width: 100%'"
          [nzBodyStyle]="{ display: 'grid', 'grid-gap': '20px', padding: '10px 30px' }"
        >
          <ng-template #bannerTitle>Manage Your Weather Alerts</ng-template>
          <div [style]="'text-align: left'">You can manage up to 5 locations</div>

          @if (state) {
            @if (state.list || state.location) {
              <ava-smart-weather-list />
            }

            @if (state.cloudSync) {
              <ava-smart-weather-loading />
            }

            @if (state.cloudSyncError) {
              <ava-smart-weather-error />
            }
          }
        </nz-card>
      }
    </div>
  `,
  imports: [NgStyle, NzButtonComponent, NzCardComponent, ListComponent, LoadingComponent, ErrorComponent, AsyncPipe],
})
export class SmartWeatherComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('container') container: ElementRef | undefined;
  containerWidth: number | undefined;
  buttonWrapperStyles: { [key: string]: string } = {};
  stateSubscription: Subscription | undefined;
  userSubscription: Subscription | undefined;
  state:
    | {
        cloudSync: boolean;
        cloudSyncError: boolean;
        list: boolean;
        location: boolean;
      }
    | undefined;
  locations: any[] = [];
  alive$ = new Subject();

  constructor(
    private gcpConnector: GcpConnectorService,
    protected jwtAuthService: JwtAuthService,
    protected windowRefService: WindowRefService,
    private swSms: SwStateMachineService,
    private stateSyncService: StateSyncService,
    private analyticsService: AnalyticsService,
  ) {}

  ngOnInit(): void {
    const userId = this.windowRefService.nativeWindow.metaData?.user?.id;
    if (userId && userId !== '1') {
      this.analyticsService.sendPage(window.location.pathname.toLowerCase() + '/smart-weather-form');
    }

    this.jwtAuthService.afAuthIdTokenResult$
      .pipe(
        tap(async (afAuthIdTokenResult) => {
          const user = this.windowRefService.nativeWindow.metaData?.user;
          const clubId = this.windowRefService.clubId;

          if (afAuthIdTokenResult && user?.firstName && user?.lastName && user?.memberNumber && clubId) {
            await this.gcpConnector.setupService(clubId);
            await this.stateSyncService.setupService(clubId, user.memberNumber);
            this.swSms.setupService(user.memberNumber, user.firstName, user.lastName);
          }
        }),
        takeUntil(this.alive$),
      )
      .subscribe();

    this.swSms.state$
      .pipe(
        tap((swSmsState) => {
          if (swSmsState) {
            this.state = {
              cloudSync: swSmsState.value === 'cloudSync',
              cloudSyncError: swSmsState.value === 'cloudSyncError',
              list: swSmsState.value === 'list',
              location: swSmsState.value === 'location',
            };
            if (swSmsState.context.locations) {
              this.locations = swSmsState.context.locations;
            }
          }
        }),
        takeUntil(this.alive$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription?.unsubscribe();
    }

    if (this.stateSubscription && this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }

    if (this.stateSyncService) {
      this.stateSyncService?.unsubscribe();
    }

    this.alive$.next(null);
    this.alive$.complete();
  }

  ngAfterViewInit(): void {
    this.onResize();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.containerWidth = this.container?.nativeElement.offsetWidth;

    this.buttonWrapperStyles = {};
    if (this.containerWidth && this.containerWidth >= 760) {
      this.buttonWrapperStyles = { 'max-width': '50%' };
    }
  }
}
